import { z } from "zod/v4";

import { AgenticLogicSdkDenialSchema } from "../sdk/denial";
import {
  AgenticLogicBestSellersQuerySchema,
  AgenticLogicInStockQuerySchema,
  AgenticLogicNewArrivalsQuerySchema,
  AgenticLogicRelatedByAttributeQuerySchema,
} from "../sdk/catalog-reads";
import {
  AgenticLogicProductOrderBySchema,
  AgenticLogicProductQuerySchema,
} from "../sdk/product-query";
import { AgenticLogicRecommendationIntentSchema } from "../sdk/product-recommendations";
import { AgenticLogicSdkReadResultSchema } from "../sdk/read-results";
import { AgenticLogicSurfaceSchema } from "../../../dtos/agentic-logic/surface";
import { AgenticLogicIntegrationClientCallSchema } from "../integration-clients/call";

// ---------------------------------------------------------------------------
// The SDK read RPC contract — what the broker's capability authorizes and the
// shape of one read request/response crossing the broker↔rule boundary.
// ---------------------------------------------------------------------------

/**
 * The reads a RULE capability may grant: structured, budgeted, request-shaped.
 * Never an Admin document or a ShopifyQL query — rule code cannot choose what
 * an Admin read selects.
 */
export const AgenticLogicRuleReadOperationKindList = [
  "storefrontQuery",
  "adminRead",
  "dataset",
  "orgExclusions",
  "productRecommendations",
  "source",
  "integrationClient",
] as const;

export const AgenticLogicRuleReadOperationKindSchema = z.enum(
  AgenticLogicRuleReadOperationKindList
);

export type AgenticLogicRuleReadOperationKind = z.infer<
  typeof AgenticLogicRuleReadOperationKindSchema
>;

/**
 * The reads a SCRIPT-SOURCE BUILD capability may grant. A build runs on a
 * schedule with no shopper present, so it may issue its own Admin GraphQL
 * document and ShopifyQL query (both run live through the trusted port, under
 * the shop's Admin budget) beside the raw Storefront, dataset and Source
 * reads a rule has. No exclusions and no recommendations: neither means
 * anything without a request.
 */
export const AgenticLogicSourceBuildReadOperationKindList = [
  "storefrontQuery",
  "dataset",
  "source",
  "adminGraphql",
  "shopifyql",
] as const;

export const AgenticLogicSourceBuildReadOperationKindSchema = z.enum(
  AgenticLogicSourceBuildReadOperationKindList
);

export type AgenticLogicSourceBuildReadOperationKind = z.infer<
  typeof AgenticLogicSourceBuildReadOperationKindSchema
>;

/** Every read the broker serves, whichever capability grants it. */
export const AgenticLogicSdkReadOperationKindList = [
  ...AgenticLogicRuleReadOperationKindList,
  "adminGraphql",
  "shopifyql",
] as const;

export const AgenticLogicSdkReadOperationKindSchema = z.enum(
  AgenticLogicSdkReadOperationKindList
);

export type AgenticLogicSdkReadOperationKind = z.infer<
  typeof AgenticLogicSdkReadOperationKindSchema
>;

export const AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION = 32;

/**
 * The most reads one script-source build may issue. Mirrors
 * `AGENTIC_LOGIC_MAX_SOURCE_BUILD_READS` in the Sources DTOs, which is the
 * authoring-facing home of the number; it is restated here so the claims
 * schema stays free of a DTO import.
 */
export const AGENTIC_LOGIC_MAX_SOURCE_BUILD_READS_CLAIM = 100;

/**
 * The most attempts one invocation's chain combinators will DRIVE. Beyond it
 * the remaining attempts are recorded `skipped` and the envelope carries a
 * warning — the chain truncates, it does not throw, because attempts are lazy
 * and ordered, so stopping at N is what a rule bounding its own loop would
 * have produced anyway.
 *
 * Deliberately equal to {@link AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION}: an
 * attempt that reads cannot outlive the read budget, so a chain driving more
 * attempts than the invocation has reads to spend is already failing at
 * read 33. Two numbers that must stay in step are one number.
 *
 * This is what makes a data-driven chain's worst case a platform fact rather
 * than something a publish gate has to prove by reading the merchant's source.
 */
export const AGENTIC_LOGIC_MAX_CHAIN_ATTEMPTS_PER_INVOCATION = 32;

function uniqueOperations<const T extends readonly [string, ...string[]]>(
  kinds: T
) {
  return z
    .array(z.enum(kinds))
    .min(1)
    .max(kinds.length)
    .refine((operations) => new Set(operations).size === operations.length, {
      error: "SDK read operations must be unique",
    });
}

/**
 * The claims of a RULE invocation's capability. Byte-compatible with every
 * token ever minted: no discriminator, strict, and `allowedOperations` may
 * only name rule reads — so a token minted before the build kinds existed and
 * one minted after parse the same way, whichever Lambda verifies it.
 */
export const AgenticLogicRuleSdkCapabilityClaimsSchema = z
  .object({
    schemaVersion: z.literal(1),
    keyId: z.string().min(1),
    orgId: z.string().min(1),
    ruleId: z.string().min(1),
    // 0 means "no published version": an authoring run recording a DRAFT.
    // Serving always stamps a real version. The invocation-state record has
    // always accepted 0 here; claims are widened to match so the two halves of
    // one invocation cannot disagree. Nothing downstream resolves a version row
    // from this — the broker only checks claims and state agree, and the
    // authority a capability carries lives entirely in `allowedOperations`,
    // `maxReads` and `expiresAtMs`, none of which vary by version.
    ruleVersion: z.number().int().nonnegative(),
    invocationId: z.uuid(),
    surface: AgenticLogicSurfaceSchema,
    placementId: z.string().min(1),
    expiresAtMs: z.number().int().positive(),
    maxReads: z
      .number()
      .int()
      .positive()
      .max(AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION),
    allowedOperations: uniqueOperations(AgenticLogicRuleReadOperationKindList),
  })
  .strict();

export type AgenticLogicRuleSdkCapabilityClaims = z.infer<
  typeof AgenticLogicRuleSdkCapabilityClaimsSchema
>;

/**
 * The claims of a SCRIPT-SOURCE BUILD's capability: one org, one Source, one
 * build (its id doubles as the invocation-state key), one deadline, one read
 * budget, and the build read kinds. Discriminated by `subject` on THIS branch
 * only, so the rule branch above stays exactly what deployed minters produce.
 */
export const AgenticLogicSourceBuildSdkCapabilityClaimsSchema = z
  .object({
    schemaVersion: z.literal(1),
    subject: z.literal("sourceBuild"),
    keyId: z.string().min(1),
    orgId: z.string().min(1),
    sourceName: z.string().min(1),
    buildId: z.uuid(),
    expiresAtMs: z.number().int().positive(),
    maxReads: z
      .number()
      .int()
      .positive()
      .max(AGENTIC_LOGIC_MAX_SOURCE_BUILD_READS_CLAIM),
    allowedOperations: uniqueOperations(
      AgenticLogicSourceBuildReadOperationKindList
    ),
  })
  .strict();

export type AgenticLogicSourceBuildSdkCapabilityClaims = z.infer<
  typeof AgenticLogicSourceBuildSdkCapabilityClaimsSchema
>;

/**
 * What a signed capability carries. A plain union, not a discriminated one:
 * the rule branch has no discriminator (see above), so it is tried first and
 * refuses the build branch's `subject` by strictness.
 */
export const AgenticLogicSdkCapabilityClaimsSchema = z.union([
  AgenticLogicRuleSdkCapabilityClaimsSchema,
  AgenticLogicSourceBuildSdkCapabilityClaimsSchema,
]);

export type AgenticLogicSdkCapabilityClaims = z.infer<
  typeof AgenticLogicSdkCapabilityClaimsSchema
>;

export type AgenticLogicSdkCapabilitySubject = "rule" | "sourceBuild";

/** Which kind of caller a capability belongs to. */
export function agenticLogicSdkCapabilitySubject(
  claims: AgenticLogicSdkCapabilityClaims
): AgenticLogicSdkCapabilitySubject {
  return "subject" in claims ? claims.subject : "rule";
}

export function isAgenticLogicSourceBuildSdkCapabilityClaims(
  claims: AgenticLogicSdkCapabilityClaims
): claims is AgenticLogicSourceBuildSdkCapabilityClaims {
  return "subject" in claims && claims.subject === "sourceBuild";
}

const AgenticLogicStorefrontQueryOperationSchema = z
  .object({
    kind: z.literal("storefrontQuery"),
    input: z
      .object({
        gql: z.string().min(1),
        variables: z.record(z.string(), z.unknown()),
        country: z.string().min(1),
        bypassCache: z.boolean().optional(),
      })
      .strict(),
  })
  .strict();

/**
 * Every Admin-backed read the port serves, keyed by `kind` with TYPED args.
 * Like `productRecommendations` below, none of these carries a document: the
 * rule ships structured args and the trusted side compiles the Admin (or
 * ShopifyQL) operation. The canonical hash of `{kind, args}` is the read's
 * cache key, so "cached per parameter combination" is a property of this
 * shape rather than of any one implementation.
 */
const AgenticLogicLegacyAdminProductQuerySchema =
  AgenticLogicProductQuerySchema.extend({
    orderBy: AgenticLogicProductOrderBySchema.optional(),
  });

export const AgenticLogicAdminReadInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("customer"), args: z.unknown() }).strict(),
  z
    .object({
      kind: z.literal("productCosts"),
      args: z.object({ productIds: z.array(z.string().min(1)) }).strict(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("productQuery"),
      args: AgenticLogicLegacyAdminProductQuerySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("bestSellers"),
      args: AgenticLogicBestSellersQuerySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("inStock"),
      args: AgenticLogicInStockQuerySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("newArrivals"),
      args: AgenticLogicNewArrivalsQuerySchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("relatedByAttribute"),
      args: AgenticLogicRelatedByAttributeQuerySchema,
    })
    .strict(),
]);

export type AgenticLogicAdminReadInput = z.infer<
  typeof AgenticLogicAdminReadInputSchema
>;

export type AgenticLogicAdminReadKind = AgenticLogicAdminReadInput["kind"];

export const AgenticLogicAdminReadKindList = [
  "customer",
  "productCosts",
  "productQuery",
  "bestSellers",
  "inStock",
  "newArrivals",
  "relatedByAttribute",
] as const satisfies readonly AgenticLogicAdminReadKind[];

const AgenticLogicAdminReadOperationSchema = z
  .object({
    kind: z.literal("adminRead"),
    input: AgenticLogicAdminReadInputSchema,
  })
  .strict();

const AgenticLogicDatasetOperationSchema = z
  .object({
    kind: z.literal("dataset"),
    key: z.string().min(1),
  })
  .strict();

const AgenticLogicOrgExclusionsOperationSchema = z
  .object({ kind: z.literal("orgExclusions") })
  .strict();

/**
 * Deliberately STRUCTURED, not GQL-carrying. `storefrontQuery` hands the rule
 * side a free-form query string; this operation hands it a product id and lets
 * the TRUSTED side compile the document, so a rule can no longer choose what
 * a recommendation read selects. That is the whole reason this is its own
 * operation kind rather than another `storefrontQuery` — the capability can
 * grant recommendations without granting arbitrary Storefront reads.
 */
const AgenticLogicProductRecommendationsOperationSchema = z
  .object({
    kind: z.literal("productRecommendations"),
    input: z
      .object({
        productId: z.string().min(1),
        intent: AgenticLogicRecommendationIntentSchema,
        // Runner-owned, exactly as on `storefrontQuery`: the SDK stamps the
        // invocation's country so the read rides `@inContext`.
        country: z.string().min(1),
      })
      .strict(),
  })
  .strict();

/**
 * A named Source (`sdk.getSource(name)`). The trusted side resolves the name
 * to its definition and runs the read it names through the same cached path
 * as the equivalent direct read, so both share one cache entry. `country` is
 * runner-owned, exactly as on `storefrontQuery`: a collection-page Source is
 * read in the shopper's market.
 */
const AgenticLogicSourceOperationSchema = z
  .object({
    kind: z.literal("source"),
    input: z
      .object({
        name: z.string().min(1),
        country: z.string().min(1),
      })
      .strict(),
  })
  .strict();

/** The longest Admin GraphQL document a build read may carry. */
export const AGENTIC_LOGIC_ADMIN_GRAPHQL_READ_MAX_LENGTH = 10_000;

/**
 * An Admin GraphQL read a SCRIPT BUILD issues: one query document and all of
 * its variables, checked by the trusted side with the same inspector an
 * `adminGraphql` Source's stored document passes on write (catalog and
 * content root fields only, never a customer, order or cost field). Never
 * grantable to a rule capability — see `AgenticLogicRuleReadOperationKindList`.
 * Runs live: a build wants the catalog as it is now, not as it was cached.
 */
export const AgenticLogicAdminGraphqlReadInputSchema = z
  .object({
    gql: z.string().min(1).max(AGENTIC_LOGIC_ADMIN_GRAPHQL_READ_MAX_LENGTH),
    variables: z.record(z.string(), z.unknown()),
  })
  .strict();

export type AgenticLogicAdminGraphqlReadInput = z.infer<
  typeof AgenticLogicAdminGraphqlReadInputSchema
>;

const AgenticLogicAdminGraphqlOperationSchema = z
  .object({
    kind: z.literal("adminGraphql"),
    input: AgenticLogicAdminGraphqlReadInputSchema,
  })
  .strict();

/** The longest ShopifyQL query a build read may carry. */
export const AGENTIC_LOGIC_SHOPIFYQL_READ_MAX_LENGTH = 4_000;

/**
 * A ShopifyQL query a SCRIPT BUILD issues, run live by the trusted side under
 * the shop's `read_reports` grant (the same gate the best-sellers rank is
 * behind) and its Admin budget. Never grantable to a rule capability.
 */
export const AgenticLogicShopifyqlReadInputSchema = z
  .object({
    query: z.string().min(1).max(AGENTIC_LOGIC_SHOPIFYQL_READ_MAX_LENGTH),
  })
  .strict();

export type AgenticLogicShopifyqlReadInput = z.infer<
  typeof AgenticLogicShopifyqlReadInputSchema
>;

const AgenticLogicShopifyqlOperationSchema = z
  .object({
    kind: z.literal("shopifyql"),
    input: AgenticLogicShopifyqlReadInputSchema,
  })
  .strict();

/**
 * A per-org third-party client call (`sdk.clients.<key>.<op>(args)`), made by
 * the trusted side on the rule's behalf with a freshly resolved credential.
 * Live by construction: never seeded, prefetched or cached, and each call
 * spends one unit of the read budget.
 */
const AgenticLogicIntegrationClientOperationSchema = z
  .object({
    kind: z.literal("integrationClient"),
    input: AgenticLogicIntegrationClientCallSchema,
  })
  .strict();

export const AgenticLogicSdkReadOperationSchema = z.discriminatedUnion("kind", [
  AgenticLogicStorefrontQueryOperationSchema,
  AgenticLogicAdminReadOperationSchema,
  AgenticLogicDatasetOperationSchema,
  AgenticLogicOrgExclusionsOperationSchema,
  AgenticLogicProductRecommendationsOperationSchema,
  AgenticLogicSourceOperationSchema,
  AgenticLogicAdminGraphqlOperationSchema,
  AgenticLogicShopifyqlOperationSchema,
  AgenticLogicIntegrationClientOperationSchema,
]);

export type AgenticLogicSdkReadOperation = z.infer<
  typeof AgenticLogicSdkReadOperationSchema
>;

export const AgenticLogicSdkReadRequestSchema = z
  .object({
    capability: z.string().min(1),
    operation: AgenticLogicSdkReadOperationSchema,
  })
  .strict();

export type AgenticLogicSdkReadRequest = z.infer<
  typeof AgenticLogicSdkReadRequestSchema
>;

export const AgenticLogicSdkReadErrorCodeList = [
  "invalid_request",
  "unauthorized",
  "read_limit_exceeded",
  "timeout",
  "read_failed",
  /** The Admin budget guard refused the read; `denial` names why. Loud, never an empty attempt. */
  "denied",
  /** Well-formed, but names a resource the org never authored (a dataset key, a Source name). Not retryable. */
  "not_found",
] as const;

export const AgenticLogicSdkReadErrorCodeSchema = z.enum(
  AgenticLogicSdkReadErrorCodeList
);

export type AgenticLogicSdkReadErrorCode = z.infer<
  typeof AgenticLogicSdkReadErrorCodeSchema
>;

export const AgenticLogicSdkReadResponseSchema = z.discriminatedUnion("ok", [
  z
    .object({
      ok: z.literal(true),
      result: AgenticLogicSdkReadResultSchema,
    })
    .strict(),
  z
    .object({
      ok: z.literal(false),
      error: z
        .object({
          code: AgenticLogicSdkReadErrorCodeSchema,
          /** Present exactly when `code` is `denied`. */
          denial: AgenticLogicSdkDenialSchema.optional(),
        })
        .strict(),
    })
    .strict(),
]);

export type AgenticLogicSdkReadResponse = z.infer<
  typeof AgenticLogicSdkReadResponseSchema
>;

/**
 * The most reads one batch may carry. Equal to the per-invocation read budget:
 * every sub-read spends one unit of it, so a larger batch could only ever be
 * refused past this point.
 */
export const AGENTIC_LOGIC_MAX_SDK_READ_BATCH_SIZE =
  AGENTIC_LOGIC_MAX_SDK_READS_PER_INVOCATION;

/**
 * Several reads in ONE hop: the reads a rule issues together (a `Promise.all`,
 * a per-cart-line fan-out) cost one SDK-read invoke instead of N. `batch` is
 * deliberately NOT an operation kind: a capability authorizes the operations
 * INSIDE it, each checked on its own, and each spends its own read-budget unit.
 */
export const AgenticLogicSdkReadBatchRequestSchema = z
  .object({
    capability: z.string().min(1),
    operations: z
      .array(AgenticLogicSdkReadOperationSchema)
      .min(1)
      .max(AGENTIC_LOGIC_MAX_SDK_READ_BATCH_SIZE),
  })
  .strict();

export type AgenticLogicSdkReadBatchRequest = z.infer<
  typeof AgenticLogicSdkReadBatchRequestSchema
>;

/**
 * What the SDK-read Lambda accepts: one read or one batch, told apart by
 * `operation` vs `operations`. Both are strict, so a tenant or state field an
 * operation does not declare is refused at the boundary, before any read.
 */
export const AgenticLogicSdkReadEventSchema = z.union([
  AgenticLogicSdkReadRequestSchema,
  AgenticLogicSdkReadBatchRequestSchema,
]);

export type AgenticLogicSdkReadEvent = z.infer<
  typeof AgenticLogicSdkReadEventSchema
>;

/**
 * A batch answers each operation positionally with the same response a single
 * read would have produced — one sub-read's refusal never fails its siblings.
 * The top-level error is reserved for what refuses the whole batch at once: a
 * malformed request or a capability that does not verify.
 */
export const AgenticLogicSdkReadBatchResponseSchema = z.discriminatedUnion(
  "ok",
  [
    z
      .object({
        ok: z.literal(true),
        results: z
          .array(AgenticLogicSdkReadResponseSchema)
          .max(AGENTIC_LOGIC_MAX_SDK_READ_BATCH_SIZE),
      })
      .strict(),
    z
      .object({
        ok: z.literal(false),
        error: z.object({ code: AgenticLogicSdkReadErrorCodeSchema }).strict(),
      })
      .strict(),
  ]
);

export type AgenticLogicSdkReadBatchResponse = z.infer<
  typeof AgenticLogicSdkReadBatchResponseSchema
>;
